/**
 * @overview Applies and removes a specified toggleClass to a selection of DOM Elements
 *
 * @module toggle
 */

import $ from 'qwery';

const TOGGLE_ATTRNAME = 'data-toggle', // attribute the component hooks in to
    TOGGLE_SELECTOR = `[${TOGGLE_ATTRNAME}]`,
    TOGGLE_ITEM_SELECTOR = '[data-toggle-item]',
    TOGGLE_WRAPPER_SELECTOR = '[data-toggle-wrapper]',
    TOGGLE_ACTIVE_CLASS = 'is-active';


function Toggle (toggleEl, options = {}) {
    // set options to our data attributes defined on the megamodal element (if there are any)
    for (const property in options.dataSet) {
        if (options.dataSet.hasOwnProperty(property) && options.dataSet[property] !== null) {
            options[property] = options.dataSet[property];
        }
    }

    /**
     * Define options – can be passed in or set as defaults
     *
     * toggleBtn
     * targets
     * toggleClass
     * toggleWrapperClass
     * toggleOff
     */
    this.options = {

        /**
         * The selector to identify the element that will initiate the toggle
         * By default this is the element to which the data-toggle is applied
         * If specified, the element specified must be lower in the DOM tree than the element the data-toggle attribute was applied
         *
         * Default: '[data-toggle]'
         * @type string
         */
        toggleBtn: options.toggleBtn || TOGGLE_SELECTOR,

        /**
         * The selector to identify the elements that will be toggled
         *
         * By default, will be the element assigned with the data-toggle-item (inside the initial component)
         * Can pass in any selector to override this, which will then toggle all elements that match this selector on the page
         *
         * Default: '[data-toggle-item]'
         * @type string
         */
        targets: ((typeof options.targets === 'object') ? options.targets.toggle : options.targets) || TOGGLE_ITEM_SELECTOR,

        enabledTargets: (options.targets && options.targets.enable ? options.targets.enable : null),

        /**
         * The class to be applied to toggleItems
         * Default: 'is-active'
         * @type string
         */
        toggleClass: options.toggleClass || TOGGLE_ACTIVE_CLASS,

        /**
         * If specified, the component will apply this class to the closest element which has the data attribute 'data-toggle-wrapper', when the toggle is active
         * @type string
         */
        toggleWrapperClass: options.toggleWrapperClass || null,

        /**
         * Takes a selector which should reference another toggle object(s)
         * When our toggle element is turned off, the specified toggle element(s) will also be forced off
         * @type string
         */
        toggleOff: options.toggleOff || null

    };

    // Find the DOM elements specific to our toggle element
    this.toggleEl = toggleEl;
    this.toggleBtn = $(this.options.toggleBtn, toggleEl)[0] || toggleEl;
    this.toggleActive = false;

    // if not the default, then expand the range for our toggle targets outside our toggle parent (makes it more flexible)
    if (this.options.targets === TOGGLE_ITEM_SELECTOR) {
        this.toggleItems = $(this.options.targets, toggleEl);
    } else {
        this.toggleItems = $(this.options.targets);
    }

    if (this.options.enabledTargets !== null) {
        this.enabledItems = $(this.options.enabledTargets);
    } else {
        this.enabledItems = [];
    }

    if (this.options.toggleWrapperClass) {
        this.toggleWrapper = toggleEl.closest(TOGGLE_WRAPPER_SELECTOR);
    }

    // initial setup of toggle element
    this.init = function () {
        if (this.toggleBtn) {
            this.toggleBtn.addEventListener('click', this.toggleEls.bind(this));
        }
        this.toggleEls();
    };

    /**
     * The function that controls the toggling
     * Called when the toggleBtn is clicked, takes an event, supresses it,
     * and then does two things:
     *
     * 1. Toggles the state classes related to this toggle object
     * 2. If the toggle object is inactive, and we have a value set for toggleOff, then toggle off these related toggle objects
     */
    this.toggleEls = function (e) {
        if (e) {
            e.preventDefault();
        }

        this.toggleItems.forEach(toggleItem => this.toggleStateClasses(toggleItem));
        this.enabledItems.forEach(toggleItem => this.toggleAttrState(toggleItem));
        this.toggleActive = !this.toggleActive;

        if (!this.toggleActive && this.options.toggleOff !== null) {
            this.toggleOff();
        }
    };


    /**
     * Takes an Element and either adds or removes the toggleClass (dependent on its current state)
     *
     * If a toggleWrapperClass has been specified in the component options, it will also add or remove this class to the toggleWrapper
     */
    this.toggleStateClasses = function (toggleItem) {
        toggleItem.classList.toggle(this.options.toggleClass);

        if (this.toggleWrapper) {
            this.toggleWrapper.classList.toggle(this.options.toggleWrapperClass);
        }
    };

    /**
     * TODO
     *
     * TODO
     */
    this.toggleAttrState = function (toggleItem) {
        if (toggleItem.hasAttribute('disabled')) {
            toggleItem.removeAttribute('disabled');
        } else {
            toggleItem.setAttribute('disabled', true);
        }
    };


    /**
     * Function that toggles off any toggle elements specified in the toggleOff component option
     *
     * Good to keep in mind that this only works when the elements are toggle components themselves
     *
     * Main use-case for this is when we have nested toggles that need to both close when the parent toggle is closed
     */
    this.toggleOff = function () {
        $(this.options.toggleOff).forEach((element) => {
            const toggleAttrs = JSON.parse(element.getAttribute('data-toggle')),
                targets = $(toggleAttrs.targets);

            // loop through the toggle targets, check if they are active, and if so, initiate a toggle off
            targets.forEach((target) => {
                if (target.classList.contains(TOGGLE_ACTIVE_CLASS)) {
                    element.click(); // fake a click event for the toggle element specified
                }
            });
        });
    };

    this.init(); // bootstrap toggle
}


const toggleEls = []; // used to keep track of existing toggle objects

/**
 * createToggle
 *
 * Creates a toggle object, with it’s associated data attribute options, and pushes it into our toggleEls array
 */
function createToggle (toggleEl) {

    // react to our toggle click event
    const config = toggleEl.getAttribute('data-toggle');

    const dataSet = config ? JSON.parse(config) : {};

    // push our toggleEl onto our list of toggle elements we’re keeping track of
    toggleEls.push(new Toggle(toggleEl, { dataSet }));
}

/**
 * getToggleComponent
 *
 * @return [DOM Element] | null
 */
function getToggleComponent (el) {
    const toggleEl = el.closest(TOGGLE_SELECTOR); // returns a toggle element if one was in the dom tree of the element clicked

    return toggleEl;
}

function isBootStrapReservedToggle (el) {
    const toggleAttr = el.getAttribute('data-toggle');

    return (toggleAttr === 'buttons' || toggleAttr === 'modal'); // returns a toggle element if one was in the dom tree of the element clicked
}

export function hideToggleItem (selector) {
    const toggleEl = getToggleComponent(selector);
    const constructed = toggleEls.filter(toggle => toggle.toggleEl === toggleEl)[0];

    if (constructed && constructed.toggleActive) {
        constructed.toggleEls();
    }
}


/**
 * The Toggle Component works slightly differently to other components
 * It registers a click listener on the document and  on a page if data attr 'data-megamodal' is present on elements in the DOM
 *
 * It does this so that it can handle Toggle elements added dynamically to the page, not just those present on page load
 */
Toggle.init = (function () {
    // add delegated click event
    document.addEventListener('click', (e) => {
        const toggleEl = getToggleComponent(e.target); // see if we can find a toggle component

        // check to see if the toggle element has already been constructed, in our toggleEls array
        const constructed = (toggleEls.filter((toggle) => (toggle.toggleEl === toggleEl)).length > 0);

        // if this is a data-toggle component, and it hasn’t been constructed, supress the click event and create a toggle object
        if (toggleEl !== null && !constructed && !isBootStrapReservedToggle(toggleEl)) {
            e.preventDefault();
            createToggle(toggleEl);
        }
    });

}());

export default Toggle;
